import {WorkflowType} from "../../utils/enums";
import {WorkflowOption} from "./types";
import {WORKFLOW_OPTIONS} from "./workflowConfig";

const ADMISSION = WorkflowType.ADMISSION;

/**
 * Legacy / alternate spellings → canonical template key.
 * Keeps lookups working if older uppercase enum values slip through.
 */
const WORKFLOW_TYPE_ALIASES: Record<string, WorkflowType> = {
  arbitration_agreement: WorkflowType.ARBITRATION,
};

const humanizeWorkflowType = (raw: string): string =>
  raw
    .split(/[-_]/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");

/** Canonical lowercase template key. Empty string if missing. */
const normalizeWorkflowType = (workflowType?: string): string => {
  if (!workflowType) return "";
  const normalized = workflowType.toLowerCase();
  return WORKFLOW_TYPE_ALIASES[normalized] ?? normalized;
};

/** Canonical lowercase key. Missing workflowType means legacy admission. */
export const resolveWorkflowType = (workflowType?: string | null): string =>
  normalizeWorkflowType(workflowType ?? undefined) || ADMISSION;

/**
 * Display label. Known types use curated copy; unknown keys are humanized
 * (`vaccination_form` → `Vaccination Form`). Missing → `Admission`.
 */
export const getWorkflowLabel = (workflowType?: string | null): string => {
  const key = resolveWorkflowType(workflowType);

  if (key in WORKFLOW_OPTIONS) {
    return WORKFLOW_OPTIONS[key as WorkflowType].label;
  }

  return humanizeWorkflowType(key);
};

const mapSingleWorkflowType = (rawType: string): WorkflowOption => {
  const normalized = normalizeWorkflowType(rawType);

  if (normalized in WORKFLOW_OPTIONS) {
    return WORKFLOW_OPTIONS[normalized as WorkflowType];
  }

  return {type: normalized, label: humanizeWorkflowType(normalized)};
};

/** Builds workflow options from template version workflowTypes (always includes admission). */
export const mapTemplateWorkflowTypesToOptions = (
  templateWorkflowTypes: ReadonlyArray<string>,
): ReadonlyArray<WorkflowOption> => {
  const types = new Set<string>([ADMISSION]);

  templateWorkflowTypes.forEach(type => {
    const normalized = normalizeWorkflowType(type);
    if (normalized && normalized !== ADMISSION) {
      types.add(normalized);
    }
  });

  return Array.from(types).map(mapSingleWorkflowType);
};

export const admissionOnlyWorkflowOption = (): WorkflowOption =>
  mapSingleWorkflowType(ADMISSION);
